import { useReducedMotion } from "@/hooks/useReducedMotion"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"
import React from "react"

export type FlowStep = {
  id: string
  title: string
  description: string
  icon?: React.ReactNode
}

type ProductFlowDiagramProps = {
  steps: FlowStep[]
  direction?: "horizontal" | "vertical"
  className?: string
}

export function ProductFlowDiagram({
  steps,
  direction = "horizontal",
  className,
}: ProductFlowDiagramProps) {
  const prefersReducedMotion = useReducedMotion()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const stepVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      y: direction === "vertical" ? 20 : 0,
      x: direction === "horizontal" ? 20 : 0,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      x: 0,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.4,
        ease: [0.4, 0, 0.2, 1] as const, // easeOut curve
      },
    },
  }

  const arrowVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.3,
        delay: 0.2,
      },
    },
  }

  if (direction === "vertical") {
    return (
      <div className={cn("space-y-6", className)}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6"
        >
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-start gap-4">
              <motion.div
                variants={stepVariants}
                className={cn(
                  "flex-1 rounded-xl border p-6",
                  semanticTokens.bg.surface,
                  semanticTokens.border.default,
                  semanticTokens.harmony.calm.shadow
                )}
              >
                <div className="flex items-start gap-4">
                  {step.icon && (
                    <div className={cn(
                      "flex-shrink-0 rounded-lg p-3",
                      semanticTokens.bg.muted
                    )}>
                      {step.icon}
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <div className={cn(
                      "text-xs font-semibold uppercase tracking-wider",
                      semanticTokens.fg.muted
                    )}>
                      Step {index + 1}
                    </div>
                    <h3 className={cn(
                      "text-lg font-bold",
                      semanticTokens.fg.default
                    )}>
                      {step.title}
                    </h3>
                    <p className={cn(
                      "text-sm leading-relaxed",
                      semanticTokens.fg.muted
                    )}>
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
              {index < steps.length - 1 && (
                <motion.div
                  variants={arrowVariants}
                  className="flex-shrink-0 pt-3"
                >
                  <ArrowRight className={cn(
                    "h-6 w-6 rotate-90",
                    semanticTokens.fg.muted
                  )} />
                </motion.div>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    )
  }

  return (
    <div className={cn("w-full", className)}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6"
      >
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <motion.div
              variants={stepVariants}
              className={cn(
                "flex-1 w-full sm:w-auto rounded-xl border p-6 text-center",
                semanticTokens.bg.surface,
                semanticTokens.border.default,
                semanticTokens.harmony.calm.shadow
              )}
            >
              {step.icon && (
                <div className={cn(
                  "inline-flex rounded-lg p-3 mb-4",
                  semanticTokens.bg.muted
                )}>
                  {step.icon}
                </div>
              )}
              <div className="space-y-2">
                <div className={cn(
                  "text-xs font-semibold uppercase tracking-wider",
                  semanticTokens.fg.muted
                )}>
                  Step {index + 1}
                </div>
                <h3 className={cn(
                  "text-lg font-bold",
                  semanticTokens.fg.default
                )}>
                  {step.title}
                </h3>
                <p className={cn(
                  "text-sm leading-relaxed",
                  semanticTokens.fg.muted
                )}>
                  {step.description}
                </p>
              </div>
            </motion.div>
            {index < steps.length - 1 && (
              <motion.div
                variants={arrowVariants}
                className="hidden sm:flex flex-shrink-0"
              >
                <ArrowRight className={cn(
                  "h-6 w-6",
                  semanticTokens.fg.muted
                )} />
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  )
}

